<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import emptyBox from '@/assets/img/emptyBox.json';
import ListGroup from '@/components/ui/ListGroup.vue';
import ListItem from '@/components/ui/ListItem.vue';
import LottieAnimation from '@/components/ui/LottieAnimation.vue';
import RoundedButton from '@/components/ui/RoundedButton.vue';
import SearchBar from '@/components/ui/SearchBar.vue';
import SearchResultItem from '@/pages/SearchPage/SearchResultItem.vue';
import type { Stop } from '@/@types/gtfs-ressource';
import type { RecentAddressSearch, RecentSearch } from '@/libs/helpers/recent-searches';
import { buildStopSearchIndex, searchIndex } from '@/libs/helpers/search';
import { networkDepartments, searchAddresses, type AddressResult } from '@/libs/geocoding';
import { useAppStore } from '@/stores/app';
import { useGtfsStore } from '@/stores/gtfs';
import { useGroupStore } from '@/stores/group';
import { useRecentSearchesStore } from '@/stores/recent-searches';

/**
 * The search, narrowed to stops and postal addresses, handing back the place picked.
 * The departure picker also offers the passenger's position; both offer a point picked on the map.
 */
const props = defineProps<{ offerPosition: boolean }>();

const emit = defineEmits<{
  selectStop: [stopId: string];
  selectAddress: [address: AddressResult];
  selectPosition: [];
  pickOnMap: [];
  close: [];
}>();

const SEARCH_DEBOUNCE_MS = 150;
/** Under this length the geocoder matches half of France: not worth the request. */
const ADDRESS_MIN_LENGTH = 3;

const appStore = useAppStore();
const gtfsStore = useGtfsStore();
const groupStore = useGroupStore();
const recentSearchesStore = useRecentSearchesStore();

const searchBar = ref<InstanceType<typeof SearchBar>>();
const query = ref<string>('');
const debouncedQuery = ref<string>('');
let debounceTimeout: number | undefined;

watch(query, value => {
  window.clearTimeout(debounceTimeout);
  if (!value) {
    debouncedQuery.value = '';
    return;
  }
  debounceTimeout = window.setTimeout(() => (debouncedQuery.value = value), SEARCH_DEBOUNCE_MS);
});

const stopIndex = computed(() =>
  buildStopSearchIndex(Object.values(gtfsStore.gtfsData.stops), gtfsStore.canonicalOf),
);
const stopResults = computed<Stop[]>(() => searchIndex(stopIndex.value, debouncedQuery.value));

type RecentPlace = { stop: Stop } | { address: RecentAddressSearch };

/** The stops and the addresses picked lately, most recent first; lines are of no use here. */
const recentPlaces = computed<RecentPlace[]>(() =>
  recentSearchesStore.items.flatMap((entry): RecentPlace[] => {
    if (entry.type === 'address') return [{ address: entry }];
    const stop = entry.type === 'stop' ? gtfsStore.gtfsData.stops[entry.id] : undefined;
    return stop ? [{ stop }] : [];
  }),
);

/** Departments of the network, the geocoder's filter; empty until known. */
const departments = ref<string[]>([]);

const addressResults = ref<AddressResult[]>([]);
const isSearchingAddresses = ref<boolean>(false);
// Answers can come back out of order: only the one for the latest text is shown.
let addressRequest = 0;
let addressController: AbortController | undefined;

watch(debouncedQuery, async value => {
  const request = ++addressRequest;
  addressController?.abort();
  addressResults.value = [];
  isSearchingAddresses.value = value.length >= ADDRESS_MIN_LENGTH;
  if (!isSearchingAddresses.value) return;
  // eslint-disable-next-line compat/compat -- iOS 11 is below our real support floor (plugin-legacy targets 'defaults, not IE 11'); .browserslistrc is intentionally broader.
  addressController = new AbortController();
  try {
    const results = await searchAddresses(
      value,
      gtfsStore.networkMapBounds,
      departments.value,
      addressController.signal,
    );
    if (request === addressRequest) addressResults.value = results;
  } catch (error) {
    // Offline, geocoder down or stalled: the stops found stay usable on their own.
    // A stalled connection timing out is network noise, not worth a warning in Datadog.
    const isTimeout = error instanceof DOMException && error.name === 'AbortError';
    if (request === addressRequest && !isTimeout) console.warn('Address search failed:', error);
  } finally {
    if (request === addressRequest) isSearchingAddresses.value = false;
  }
});

const hasResults = computed<boolean>(() => stopResults.value.length > 0 || addressResults.value.length > 0);

function select(entry: RecentSearch) {
  recentSearchesStore.add(entry);
  emit('selectStop', entry.id);
}

function selectAddress(address: AddressResult) {
  const { id, name, locality, lat, lon } = address;
  recentSearchesStore.add({ type: 'address', id, name, locality, lat, lon });
  emit('selectAddress', address);
}

function selectRecentAddress({ id, name, locality, lat, lon }: RecentAddressSearch) {
  selectAddress({ id, label: `${name} ${locality}`.trim(), name, locality, lat, lon });
}

let unregisterBackHandler: (() => void) | undefined;

onMounted(() => {
  searchBar.value?.focus();
  const groupId = groupStore.group?.group_id;
  if (groupId) {
    networkDepartments(groupId, Object.values(gtfsStore.gtfsData.stops)).then(
      found => (departments.value = found),
    );
  }
  unregisterBackHandler = appStore.registerBackHandler(() => {
    emit('close');
    return true;
  });
});

onBeforeUnmount(() => {
  window.clearTimeout(debounceTimeout);
  addressRequest++;
  addressController?.abort();
  unregisterBackHandler?.();
});
</script>

<template>
  <!-- Out of the itinerary page, which lets the taps through to the map. -->
  <Teleport to="body">
    <div class="place-picker">
      <div class="place-picker__bar">
        <RoundedButton
          :aria-label="$t('common.back')"
          icon="chevron-left"
          type="ghost"
          @click="emit('close')"
        />
        <SearchBar ref="searchBar" v-model="query" :placeholder="$t('itinerary.searchPlace')" />
      </div>

      <div class="place-picker__results">
        <button
          v-if="props.offerPosition && !debouncedQuery"
          class="place-picker__option"
          type="button"
          @click="emit('selectPosition')"
        >
          <ListItem type="recent-search" icon="map-pin" :label="$t('itinerary.yourPosition')" />
        </button>
        <button v-if="!debouncedQuery" class="place-picker__option" type="button" @click="emit('pickOnMap')">
          <ListItem type="recent-search" icon="map-pin-plus" :label="$t('itinerary.pickOnMap.option')" />
        </button>

        <template v-if="debouncedQuery">
          <ListGroup v-if="stopResults.length" :label="$t('search.sectionStops')">
            <SearchResultItem
              v-for="stop in stopResults"
              :key="stop.stop_id"
              :stop="stop"
              selectable
              @select="select"
            />
          </ListGroup>
          <ListGroup v-if="addressResults.length" :label="$t('itinerary.sectionAddresses')">
            <button
              v-for="address in addressResults"
              :key="address.id"
              class="place-picker__option"
              type="button"
              @click="selectAddress(address)"
            >
              <ListItem
                type="recent-search"
                icon="map-pin"
                :label="address.name"
                :description="address.locality"
              />
            </button>
          </ListGroup>
          <div v-if="!hasResults && !isSearchingAddresses" class="place-picker__empty">
            {{ $t('search.noResults') }}
            <LottieAnimation :animation-data="emptyBox" class="place-picker__empty-animation" />
          </div>
          <div v-else-if="!hasResults" class="place-picker__empty">
            {{ $t('itinerary.searchingAddresses') }}
          </div>
        </template>

        <ListGroup v-else-if="recentPlaces.length" :label="$t('search.recent')">
          <template v-for="place in recentPlaces">
            <SearchResultItem
              v-if="'stop' in place"
              :key="`stop-${place.stop.stop_id}`"
              :stop="place.stop"
              selectable
              @select="select"
            />
            <button
              v-else
              :key="`address-${place.address.id}`"
              class="place-picker__option"
              type="button"
              @click="selectRecentAddress(place.address)"
            >
              <ListItem
                type="recent-search"
                icon="map-pin"
                :label="place.address.name"
                :description="place.address.locality"
              />
            </button>
          </template>
        </ListGroup>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss">
.place-picker {
  background-color: $color-white;
  display: flex;
  flex-direction: column;
  inset: 0;
  padding-top: var(--status-bar-band);
  pointer-events: auto;
  position: fixed;
  z-index: 4;

  &__bar {
    align-items: center;
    display: flex;
    gap: $spacing-2;
    padding: 12px $spacing-4 0 $spacing-2;

    .searchbar {
      flex: 1;
      margin-bottom: 0;
      min-width: 0;
    }
  }

  &__results {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: $spacing-3;
    overflow-y: auto;
    padding: $spacing-4 0 calc(#{$spacing-4} + env(safe-area-inset-bottom) + var(--keyboard-height, 0px));
  }

  &__option {
    background: none;
    border: none;
    padding: 0;
    text-align: left;
    width: 100%;

    &:active .list-item {
      background-color: $color-neutral-lower;
    }
  }

  &__empty {
    align-items: center;
    color: $color-neutral-higher;
    display: flex;
    flex-direction: column;
    font: $text-small;
    gap: $spacing-4;
    letter-spacing: $text-small-spacing;
    padding: $spacing-2 $spacing-5;
    text-align: center;
  }

  &__empty-animation {
    aspect-ratio: 1;
    width: min(160px, 50vw);
  }
}
</style>
